import { useState } from "react"

import { WheelPicker, type WheelOption } from "@/components/ui/wheel-picker"
import type { StepComponentProps } from "./step-contract"

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const NOW = new Date()
const TODAY = { year: NOW.getFullYear(), month: NOW.getMonth() + 1, day: NOW.getDate() }
const FIRST_YEAR = 1940
/** The year wheel opens here — the "Year" placeholder is anchored just after it. */
const YEAR_ANCHOR = 2000

/** Days in a 1-based month, accounting for leap years (day 0 of next month). */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

const pad = (n: string) => n.padStart(2, "0")

/** An empty string in any field means "not picked yet" → the placeholder shows. */
interface Parts {
  month: string
  day: string
  year: string
}

function parseValue(value: string): Parts {
  const [year, month, day] = value.split("-")
  if (year && month && day) return { year, month: String(Number(month)), day: String(Number(day)) }
  return { month: "", day: "", year: "" }
}

/**
 * Shared date-of-birth wheel for every quiz. Opens UNSELECTED: each column starts
 * on a "Month" / "Day" / "Year" placeholder and the stored answer stays empty (so
 * the `required` validator keeps Continue disabled) until all three columns are
 * chosen — no silent default date. `dateMode: "dob"` forbids future months/days of
 * the current year. Built on the generic WheelPicker.
 */
export function DateStep({ step, value, onChange }: StepComponentProps) {
  const isDob = step.dateMode === "dob"
  const [parts, setParts] = useState<Parts>(() =>
    parseValue(typeof value === "string" ? value : ""),
  )

  const yearNum = parts.year ? Number(parts.year) : null
  const monthNum = parts.month ? Number(parts.month) : null

  // A DOB can never reach into a future month/day of the current year.
  const maxMonth = isDob && yearNum === TODAY.year ? TODAY.month : 12
  const baseDays = monthNum && yearNum ? daysInMonth(yearNum, monthNum) : 31
  const maxDay =
    isDob && yearNum === TODAY.year && monthNum === TODAY.month
      ? Math.min(baseDays, TODAY.day)
      : baseDays

  const monthOptions: WheelOption[] = [
    { label: "Month", value: "" },
    ...MONTHS.slice(0, maxMonth).map((label, i) => ({ label, value: String(i + 1) })),
  ]
  const dayOptions: WheelOption[] = [
    { label: "Day", value: "" },
    ...Array.from({ length: maxDay }, (_, i) => ({ label: String(i + 1), value: String(i + 1) })),
  ]
  // Figma opens the year wheel "between 2000 and 2001": the placeholder is
  // anchored right after YEAR_ANCHOR (not at the very top), so the wheel rests on
  // a plausible birth year instead of 1940. Month/Day keep their placeholder on top.
  const yearOptions: WheelOption[] = []
  for (let y = FIRST_YEAR; y <= TODAY.year; y++) {
    yearOptions.push({ label: String(y), value: String(y) })
    if (y === YEAR_ANCHOR) yearOptions.push({ label: "Year", value: "" })
  }

  const handleChange = (key: string, next: string) => {
    const updated: Parts = { ...parts, [key]: next }

    // Once a year+month are both known, re-clamp so a stale day can't survive a
    // shorter month (Jan 31 → Feb) and a DOB can't point past today.
    if (updated.year && updated.month) {
      const y = Number(updated.year)
      let m = Number(updated.month)
      if (isDob && y === TODAY.year && m > TODAY.month) {
        m = TODAY.month
        updated.month = String(m)
      }
      const dim = daysInMonth(y, m)
      const cap = isDob && y === TODAY.year && m === TODAY.month ? Math.min(dim, TODAY.day) : dim
      if (updated.day && Number(updated.day) > cap) updated.day = String(cap)
    }

    setParts(updated)
    onChange(
      updated.month && updated.day && updated.year
        ? `${updated.year}-${pad(updated.month)}-${pad(updated.day)}`
        : "",
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <WheelPicker
        columns={[
          { key: "month", options: monthOptions, flex: 1.4 },
          { key: "day", options: dayOptions },
          { key: "year", options: yearOptions },
        ]}
        value={{ month: parts.month, day: parts.day, year: parts.year }}
        onChange={handleChange}
      />
    </div>
  )
}
